import { colorPalettes } from '@/utils/color.js'
import deviceType from '@/utils/device.js'
// import { deviceDetect } from 'mobile-device-detect'

function getRawQuery() {
  return Object.fromEntries(
    location.hash
      .replace(/^((?!\?).)+\??/, '')
      .split('&')
      .filter(d => d)
      .map(field => field.split('=').map(decodeURIComponent))
  )
}

function getResponsiveFontSize(value) {
  let available
  if (deviceType === 'mobile') {
    available = [11, 14]
  } else {
    available = [12, 16]
  }

  return available[available.indexOf(value)] || available[0]
}

function bootstrapState() {
  const query = getRawQuery()

  const fullfilledQuery = {
    mainCubeId: query.mainCubeId || null,
    pairCubeId: query.pairCubeId || null,
    sizeCubeId: query.sizeCubeId || null,
    mainIndId: query.mainIndId || null,
    pairIndId: query.pairIndId || null,
    sizeIndId: query.sizeIndId || null,
    mainBreakdowns: Object.fromEntries(
      (query.mainBreakdowns || '')
        .split('.')
        .filter(d => d)
        .map(dimensionDefn => decodeURIComponent(dimensionDefn).split(':'))
    ),
    pairBreakdowns: Object.fromEntries(
      (query.pairBreakdowns || '')
        .split('.')
        .filter(d => d)
        .map(dimensionDefn => decodeURIComponent(dimensionDefn).split(':'))
    ),
    sizeBreakdowns: Object.fromEntries(
      (query.sizeBreakdowns || '')
        .split('.')
        .filter(d => d)
        .map(dimensionDefn => decodeURIComponent(dimensionDefn).split(':'))
    ),
    lollipop: query.lollipop || null,
    lollipopOpts: (query.lollipopOpts || '').split('.').filter(d => d),
    countries: (query.countries || '').split('.').filter(d => d),
    countryFilter: query.countryFilter === 'true',
    time: (query.time || '')
      .split('.')
      .filter(d => d)
      .map(d => parseInt(d, 10)),
    chart: query.chart,
    fontSize: getResponsiveFontSize(query.fontSize),
    palette: colorPalettes[query.palette] ? query.palette : 'normal',
    lastDates: query.lastDates !== 'false',
    timeScale: query.timeScale || '', // || 'P1Y',
    mainUnit: query.mainUnit || '',
    pairUnit: query.pairUnit || '',
    sizeUnit: query.sizeUnit || '',
    flip: query.flip === void 0 || query.flip === 'false' ? false : query.flip,
    fullLabel: query.fullLabel === 'true',
  }

  if (!fullfilledQuery.chart) {
    if (/stan_/i.test(fullfilledQuery.mainCubeId)) {
      fullfilledQuery.chart = 'linechart'
    } else if (/DF_NDSC$/i.test(fullfilledQuery.mainCubeId)) {
      fullfilledQuery.chart = 'heatmap'
    } else if (fullfilledQuery.mainCubeId !== null) {
      fullfilledQuery.chart = 'barchart'
    }
  }

  return fullfilledQuery
}

export default {
  state: bootstrapState(),
  getters: {
    dataQuery: state => (slot, allDimensions, timeDimension, { isNDSC, hasLollipop }) => {
      // Waits for a store.cubeMeta 'alDimensions' array and return an object with the cubeId and an array with dimension values filtered by store.query state values formated as an array of collections of key:value pairs '[[{key:val}],[{key:val}]]'
      return {
        cubeId: state[slot + 'CubeId'],
        time: timeDimension.options.reduce(
          (range, year) => {
            // For the query, get always the whole time range. The time is filtered internally, not on the request
            return [
              Math.min(range[0], parseInt(year.id, 10) || range[0]),
              Math.max(range[1], parseInt(year.id, 10) || range[1]),
            ]
          },
          [Infinity, -Infinity]
        ),
        dimensions: allDimensions.map(dim => {
          if (dim.type === 'country') {
            // Always return an empty array of countries to fetch them all
            return []
          } else if (dim.type === 'indicator') {
            return [state[slot + 'IndId']]
          } else if (/breakdown/i.test(dim.type)) {
            // Get the URL value if exists, else get the first dim option as fallback value
            const defaultOpt = dim.options.find(opt => opt.isDefault) || dim.options[0]
            const ids = [state[slot + 'Breakdowns'][dim.id] ?? defaultOpt.id]
            if (isNDSC === true) {
              // In case is DF_NDSC cube, fetch all the data and filter it on the client
              return []
            }

            if (hasLollipop === false) {
              return ids
            }

            let lollipop = state.lollipop
            if (!lollipop) {
              const firstBreakdownDim = allDimensions.filter(dim => /breakdown/.test(dim.type))[0]
              if (firstBreakdownDim) {
                lollipop = firstBreakdownDim.id
              }
            }

            if (lollipop === dim.id) {
              state.lollipopOpts.forEach(id => {
                if (!ids.includes(id)) {
                  ids.push(id)
                }
              })
            }
            return ids
          } else {
            return []
          }
        }),
      }
    },
    queryTime(state) {
      return state.time.map(v => new Date(+v))
    },
    queryMutations: state => (constraints, slot) => {
      const query = state[slot + 'Breakdowns']
      if (!Object.keys(query).length) return

      return constraints.reduce((mutation, dim) => {
        if (query[dim.id] && !dim.values.includes(query[dim.id])) {
          mutation[dim.id] = dim.values[0]
        }

        return mutation
      }, {})
    },
  },
  mutations: {
    queryParams(state, params) {
      for (let key in state) {
        state[key] = params[key] || state[key]
      }
    },
    resetQuery(state, query) {
      Object.assign(state, { ...state, ...query })
    },
    mainCubeId(state, value) {
      state.mainCubeId = value
      state.mainIndId = null
      state.mainBreakdowns = {}

      state.countries = []
      state.lollipop = null
      state.lollipopOpts = []
    },
    pairCubeId(state, value) {
      state.pairCubeId = value
      state.pairIndId = null
      state.pairBreakdowns = {}
    },
    sizeCubeId(state, value) {
      state.sizeCubeId = value
      state.sizeIndId = null
      state.sizeBreakdowns = {}
    },
    mainIndId(state, value) {
      state.mainIndId = value
    },
    pairIndId(state, value) {
      state.pairIndId = value
    },
    sizeIndId(state, value) {
      state.sizeIndId = value
    },
    mainBreakdowns(state, data) {
      state.mainBreakdowns = data
    },
    sizeBreakdowns(state, data) {
      state.sizeBreakdowns = data
    },
    pairBreakdowns(state, data) {
      state.pairBreakdowns = data
    },
    lollipop(state, value) {
      state.lollipop = value
      state.lollipopOpts = []
    },
    lollipopOpts(state, value) {
      state.lollipopOpts = value
    },
    countries(state, countries) {
      state.countries = countries
    },
    countryFilter(state, filter) {
      state.countryFilter = filter
    },
    time(state, time) {
      state.time = time.map(v => (Object.prototype.isPrototypeOf.call(Date, v) ? v.getTime() : +v))
    },
    chart(state, chart) {
      state.chart = chart
    },
    fontSize(state, value) {
      state.fontSize = value
    },
    palette(state, value) {
      state.palette = Object.prototype.hasOwnProperty.call(colorPalettes, value) ? value : 'normal'
    },
    lastDates(state, value) {
      state.lastDates = value
    },
    timeScale(state, value) {
      state.timeScale = value
    },
    mainUnit(state, value) {
      state.mainUnit = value
    },
    pairUnit(state, value) {
      state.pairUnit = value
    },
    sizeUnit(state, value) {
      state.sizeUnit = value
    },
    flip(state, value) {
      state.flip = value
    },
    fullLabel(state, value) {
      state.fullLabel = Boolean(value)
    },
    rebootQuery(state) {
      Object.entries(bootstrapState()).forEach(([k, v]) => (state[k] = v))
    },
  },
  actions: {
    resetChartQuery({ commit, state }, defaults) {
      const wiped = Object.fromEntries(
        Object.entries(state).map(([k, v]) => {
          return [k, Array.isArray(v) ? [] : Object.prototype.isPrototypeOf(v) ? {} : null]
        })
      )

      commit('resetQuery', {
        ...wiped,
        flip: false,
        lastDates: true,
        timeScale: 'A', // P1Y',
        fontSize: '12',
        palette: 'normal',
        mainCubeId: state.mainCubeId,
        mainIndId: state.mainIndId,
        ...defaults,
      })
    },
  },
}
